"use client";

import * as React from "react";
import * as yup from "yup";
import Link from "next/link";
import { useSearchParams, useRouter } from "next/navigation";
import { ArrowLeftIcon } from "@heroicons/react/solid";
import { confirmResetPassword } from "#src/utils/aws-amplify-auth";
import Alert, { AlertProps } from "#src/app/(app)/_components/alert/Alert";
import Button from "#src/app/(app)/_components/button/Button";
import PasswordInput from "#src/app/(auth)/_components/password-input/PasswordInput";

type ResetPasswordFormValues = yup.InferType<typeof resetPasswordSchema>;

const resetPasswordSchema = yup.object({
  password: yup
    .string()
    .nullable()
    .min(8, "Password must be at least 8 characters long")
    .matches(/^(?:.*\d)/, "Password must contain at least one number")
    .matches(
      /^(?:.*[A-Z])/,
      "Password must contain at least one uppercase letter"
    )
    .matches(
      /^(?:.*[^\da-zA-Z])/,
      "Password must contain at least one special character"
    )
    .required("Password is required"),
});

export default function ResetPassword() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const params = Object.fromEntries(searchParams.entries());

  const [errors, setErrors] = React.useState<any>({});
  const [loading, setLoading] = React.useState(false);
  const [alert, setAlert] = React.useState<AlertProps>();

  const submitNewPassword = async ({ password }: ResetPasswordFormValues) => {
    try {
      await resetPasswordSchema.validate({ password });

      setErrors({});

      await confirmResetPassword({
        username: params.user,
        confirmationCode: params.code,
        newPassword: password,
      });

      router.push("/login");
    } catch (error) {
      console.error(error);

      setLoading(false);

      if (error instanceof yup.ValidationError) {
        setErrors({
          password: { message: error.message },
        });
      } else {
        setAlert({
          status: Alert.Status.CRITICAL,
          text: error.message,
        });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setAlert(undefined);
    setLoading(true);

    const formData = new FormData(e.target as HTMLFormElement);
    const { password } = Object.fromEntries(formData.entries());

    await submitNewPassword({ password: password.toString() });
  };

  if (params.code && params.user) {
    return (
      <form
        className="flex flex-col justify-center space-y-6"
        onSubmit={handleSubmit}
      >
        <h1 className="block w-full text-2xl text-center text-gray-800">
          Reset your password
        </h1>

        {alert && (
          <div className="mb-6" data-testid="page-alert">
            <Alert status={alert.status} text={alert.text} icon={alert.icon} />
          </div>
        )}

        <PasswordInput
          errors={errors}
          hint="Passwords should be 8 characters or more and contain at least one number, one uppercase character, and one special character"
        />

        <div className="flex justify-between">
          <Link href="/login" className="flex items-center gap-2">
            <ArrowLeftIcon width="16" />
            Back
          </Link>

          <Button loading={loading} size={Button.Size.LARGE} disabled={loading}>
            Submit
          </Button>
        </div>
      </form>
    );
  }
}
